import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Activity,
  BarChart3,
  Building2,
  ChevronLeft,
  ChevronRight,
  ClipboardCheck,
  Copy,
  Download,
  Eye,
  File,
  FileText,
  Folder,
  FolderOpen,
  FolderPlus,
  FolderUp,
  HardDrive,
  History,
  Image as ImageIcon,
  LayoutGrid,
  List,
  LoaderCircle,
  MessageSquare,
  MoreHorizontal,
  Move,
  Plus,
  RefreshCw,
  RotateCcw,
  Search,
  Share2,
  SlidersHorizontal,
  Star,
  Trash2,
  Upload,
  UploadCloud,
  Users,
  X,
} from 'lucide-react'
import { api, getApiErrorMessage } from '../lib/api'
import { useToast } from '../ui/ToastProvider'
import { useAuth } from '../auth/AuthContext'
import {
  DialogBackdrop,
  DialogBody,
  DialogFooter,
  DialogHeader,
  DialogPanel,
} from '../ui/DialogLayout'

import OrgLibraryCollaborationPanel from './orgLibrary/OrgLibraryCollaborationPanel'
import OrgLibraryFolderTree from './orgLibrary/OrgLibraryFolderTree'
import OrgLibraryInsightsPanel from './orgLibrary/OrgLibraryInsightsPanel'
import OrgLibraryPermissionDialog from './orgLibrary/OrgLibraryPermissionDialog'
import OrgLibraryWorkflowPanel from './orgLibrary/OrgLibraryWorkflowPanel'

const PAGE_SIZE = 50
const ACCEPTED_FILE_TYPES = '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.hwp,.hwpx,.jpg,.jpeg,.png,.gif,.webp'
const ORG_LIBRARY_ITEMS_MIME = 'application/x-org-library-items'
const BASE_LIBRARY_VIEWS = [
  { key: 'ALL', label: '전체 자료', icon: FolderOpen },
  { key: 'MY', label: '내 자료', icon: Users },
  { key: 'SHARED', label: '공유받은 자료', icon: Share2 },
  { key: 'RECENT', label: '최근 사용', icon: RefreshCw },
  { key: 'STARRED', label: '중요 자료', icon: Star },
  { key: 'REQUESTS', label: '업로드 요청', icon: UploadCloud },
  { key: 'REVIEWS', label: '검토 현황', icon: ClipboardCheck },
  { key: 'TRASH', label: '휴지통', icon: Trash2 },
]
const MANAGER_LIBRARY_VIEWS = [
  { key: 'INSIGHTS', label: '이용·보안 현황', icon: BarChart3 },
  { key: 'ACTIVITY', label: '활동 기록', icon: Activity },
]
const SPECIAL_VIEWS = new Set(['REQUESTS', 'REVIEWS', 'INSIGHTS', 'ACTIVITY'])
const TYPE_FILTERS = [
  ['', '모든 형식'],
  ['PDF', 'PDF'],
  ['IMAGE', '이미지'],
  ['DOCUMENT', '문서'],
  ['SPREADSHEET', '스프레드시트'],
  ['PRESENTATION', '프레젠테이션'],
  ['TEXT', '텍스트'],
]
const SORT_OPTIONS = [
  ['NAME:ASC', '이름순'],
  ['UPDATED:DESC', '최근 수정순'],
  ['SIZE:DESC', '큰 파일순'],
  ['OWNER:ASC', '소유자순'],
]

function unwrap(response, fallback) {
  return response?.data?.data ?? response?.data ?? fallback
}

function formatBytes(value) {
  const bytes = Number(value || 0)
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1)
  const scaled = bytes / (1024 ** index)
  const digits = index === 0 || scaled >= 10 || Number.isInteger(scaled) ? 0 : 1
  return `${scaled.toFixed(digits)} ${units[index]}`
}

function formatDate(value) {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '-'
  return new Intl.DateTimeFormat('ko-KR', {
    year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
  }).format(date)
}

function itemKey(kind, id) {
  return `${kind}:${id}`
}

function itemIcon(item, kind, className = 'h-5 w-5') {
  if (kind === 'folder') return <Folder className={`${className} fill-amber-100 text-amber-600`} />
  if (item.typeLabel === '이미지') return <ImageIcon className={`${className} text-cyan-600`} />
  if (item.typeLabel?.includes('문서')) return <FileText className={`${className} text-blue-600`} />

  return <File className={`${className} text-slate-500`} />
}

const SCAN_STATUS_META = {
  VERIFIED: { label: '안전', className: 'border-emerald-200 bg-emerald-50 text-emerald-700' },
  PENDING: { label: '검사 중', className: 'border-cyan-200 bg-cyan-50 text-cyan-700' },
  THREAT: { label: '위험 감지', className: 'border-red-200 bg-red-50 text-red-700' },
  UNSUPPORTED: { label: '검사 불가', className: 'border-amber-200 bg-amber-50 text-amber-700' },
  ACCESS_DENIED: { label: '검사 실패', className: 'border-amber-200 bg-amber-50 text-amber-700' },
  FAILED: { label: '검사 실패', className: 'border-amber-200 bg-amber-50 text-amber-700' },
  NOT_SCANNED: { label: '검사 안 함', className: 'border-slate-200 bg-slate-50 text-slate-600' },
  UNKNOWN: { label: '확인 대기', className: 'border-slate-200 bg-slate-50 text-slate-600' },
}

function scanStatusMeta(status) {
  return SCAN_STATUS_META[String(status || '').toUpperCase()] || SCAN_STATUS_META.UNKNOWN
}

function fileContentAvailable(item) {
  if (item?.contentAvailable === true) return true
  if (item?.contentAvailable === false) return false
  const status = String(item?.scanStatus || '').toUpperCase()
  return !status || status === 'VERIFIED' || status === 'NOT_SCANNED'
}

function scanUnavailableMessage(item) {
  const status = String(item?.scanStatus || '').toUpperCase()
  if (!status || status === 'PENDING' || status === 'UNKNOWN') return '파일 안전 검사가 진행 중입니다.'
  return '안전 검사를 통과하지 못한 파일은 열거나 다운로드할 수 없습니다.'
}

function ScanStatusBadge({ status }) {
  if (!status) return null
  const meta = scanStatusMeta(status)
  return (
    <span className={`inline-flex h-6 shrink-0 items-center rounded border px-1.5 text-xs font-medium ${meta.className}`}>
      {meta.label}
    </span>
  )
}

function IconButton({ title, children, className = '', ...props }) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-800 disabled:cursor-not-allowed disabled:opacity-40 ${className}`}
      {...props}
    >
      {children}
    </button>
  )
}

function putDirectFile(session, file, onProgress) {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest()
    request.open(session.method || 'PUT', session.uploadUrl)
    for (const [name, value] of Object.entries(session.headers || {})) {
      request.setRequestHeader(name, value)
    }
    request.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress?.(Math.round((event.loaded / event.total) * 100))
    }
    request.onload = () => {
      if (request.status >= 200 && request.status < 300) resolve()
      else reject(new Error())
    }
    request.onerror = () => reject(new Error())
    request.onabort = () => reject(new Error())
    request.send(file)
  })
}

const sleep = (milliseconds) => new Promise((resolve) => window.setTimeout(resolve, milliseconds))


export default function OrgLibraryPage() {
  const { show } = useToast()
  const { user } = useAuth()
  const uploadInputRef = useRef(null)
  const folderUploadInputRef = useRef(null)
  const versionInputRef = useRef(null)
  const requestRef = useRef(0)
  const isOperator = (user?.roles || []).includes('OPERATOR')
    || (user?.effectiveRoles || []).includes('OPERATOR')
  const apiRoot = isOperator ? '/ops/org-library' : '/org-library'
  const deepLinkRootOrgId = useMemo(
    () => new URLSearchParams(window.location.search).get('rootOrgId'),
    [],
  )
  const deepLinkFileId = useMemo(() => new URLSearchParams(window.location.search).get('fileId'), [])
  const deepLinkHandledRef = useRef(false)
  const [options, setOptions] = useState([])
  const [selectedRootOrgId, setSelectedRootOrgId] = useState('')
  const [activeView, setActiveView] = useState('ALL')
  const [currentFolderId, setCurrentFolderId] = useState(null)
  const [page, setPage] = useState(0)
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [contents, setContents] = useState(null)
  const [activities, setActivities] = useState(null)
  const [loading, setLoading] = useState(true)
  const [versionDialog, setVersionDialog] = useState(null)
  const [versions, setVersions] = useState([])
  const [versionsLoading, setVersionsLoading] = useState(false)
  const [error, setError] = useState('')
  const [displayMode, setDisplayMode] = useState('list')
  const [selectedItems, setSelectedItems] = useState(new Set())
  const [newMenuOpen, setNewMenuOpen] = useState(false)
  const [editDialog, setEditDialog] = useState(null)
  const [editValue, setEditValue] = useState('')
  const [moveDialog, setMoveDialog] = useState(null)
  const [moveTarget, setMoveTarget] = useState('')
  const [busy, setBusy] = useState(false)
  const [preview, setPreview] = useState(null)
  const [folderTreeItems, setFolderTreeItems] = useState([])
  const [uploadCapabilities, setUploadCapabilities] = useState(null)
  const [sortValue, setSortValue] = useState('NAME:ASC')
  const [typeFilter, setTypeFilter] = useState('')
  const [tagFilter, setTagFilter] = useState('')
  const [permissionFolder, setPermissionFolder] = useState(null)
  const [collaborationFile, setCollaborationFile] = useState(null)
  const [contextMenu, setContextMenu] = useState(null)
  const [uploadProgress, setUploadProgress] = useState(null)
  const [failedUploads, setFailedUploads] = useState([])
  const [pendingUploadRequest, setPendingUploadRequest] = useState(null)
  const [dropActive, setDropActive] = useState(false)
  const [dropTargetFolderId, setDropTargetFolderId] = useState(null)

  const selectedOption = useMemo(
    () => options.find((option) => String(option.rootOrgId) === String(selectedRootOrgId)) || null,
    [options, selectedRootOrgId],
  )
  const canManageAll = isOperator || selectedOption?.canManageAll === true
  const libraryViews = useMemo(
    () => canManageAll ? [...BASE_LIBRARY_VIEWS, ...MANAGER_LIBRARY_VIEWS] : BASE_LIBRARY_VIEWS,
    [canManageAll],
  )
  const workspace = contents?.workspace || selectedOption
  const readOnly = Boolean(workspace?.readOnly)
  const canManageCurrentFolder = contents?.currentFolderManageable !== false
  const canDropToCurrentFolder = !readOnly && canManageCurrentFolder && activeView === 'ALL'
  const folders = useMemo(() => (Array.isArray(contents?.folders) ? contents.folders : []), [contents?.folders])
  const files = useMemo(() => (Array.isArray(contents?.files) ? contents.files : []), [contents?.files])
  const items = useMemo(() => [
    ...folders.map((item) => ({ kind: 'folder', item })),
    ...files.map((item) => ({ kind: 'file', item })),
  ], [files, folders])
  const quota = Number(workspace?.quotaBytes || 0)
  const used = Number(workspace?.usedBytes || 0)
  const usagePercent = quota > 0 ? Math.min(100, (used / quota) * 100) : 0

  const loadWorkspaces = useCallback(async () => {
    try {
      const response = await api.get(`${apiRoot}/workspaces`, {
        __silent: true, __suppressErrorPageRedirect: true,
      })
      const loaded = unwrap(response, [])
      const next = Array.isArray(loaded) ? loaded : []
      setOptions(next)
      setSelectedRootOrgId((previous) => (
        next.some((option) => String(option.rootOrgId) === String(previous)) ? previous
          : next.some((option) => String(option.rootOrgId) === String(deepLinkRootOrgId))
            ? String(deepLinkRootOrgId)
            : String(next[0]?.rootOrgId || '')
      ))
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, '조직 자료실 목록을 불러오지 못했습니다.'))
    }
  }, [apiRoot, deepLinkRootOrgId])

  const loadWorkspaceSupport = useCallback(async () => {
    if (!selectedRootOrgId) {
      setFolderTreeItems([])
      setUploadCapabilities(null)
      return
    }
    try {
      const [treeResponse, capabilityResponse] = await Promise.all([
        api.get(`${apiRoot}/workspaces/${selectedRootOrgId}/folder-tree`, {
          __silent: true, __suppressErrorPageRedirect: true,
        }),
        api.get(`${apiRoot}/workspaces/${selectedRootOrgId}/upload-capabilities`, {
          __silent: true, __suppressErrorPageRedirect: true,
        }),
      ])
      setFolderTreeItems(unwrap(treeResponse, []))
      setUploadCapabilities(unwrap(capabilityResponse, null))
    } catch {
      setFolderTreeItems([])
      setUploadCapabilities(null)
    }
  }, [apiRoot, selectedRootOrgId])


  const loadData = useCallback(async () => {
    if (!selectedRootOrgId) {
      setLoading(false)
      setContents(null)
      return
    }
    const version = ++requestRef.current
    setLoading(true)
    setError('')
    try {
      if (activeView === 'ACTIVITY') {
        const response = await api.get(`${apiRoot}/workspaces/${selectedRootOrgId}/activities`, {
          params: { page, size: 30 }, __silent: true, __suppressErrorPageRedirect: true,
        })
        if (version === requestRef.current) setActivities(unwrap(response, null))
        return
      }
      if (SPECIAL_VIEWS.has(activeView)) return
      const [sort, direction] = sortValue.split(':')
      const params = {
        view: activeView,
        page,
        size: PAGE_SIZE,
        sort,
        direction,
      }
      if (typeFilter) params.type = typeFilter
      if (tagFilter.trim()) params.tag = tagFilter.trim()
      if (activeView === 'ALL' && currentFolderId != null) params.folderId = currentFolderId
      if (debouncedSearch) params.search = debouncedSearch
      const response = await api.get(`${apiRoot}/workspaces/${selectedRootOrgId}/contents`, {
        params, __silent: true, __suppressErrorPageRedirect: true,
      })
      if (version !== requestRef.current) return
      const payload = unwrap(response, null)
      setContents(payload)
      if (payload?.workspace) {
        setOptions((previous) => previous.map((option) => (
          String(option.rootOrgId) === String(payload.workspace.rootOrgId)
            ? { ...option, ...payload.workspace, initialized: true }
            : option
        )))
      }
    } catch (requestError) {
      if (version !== requestRef.current) return
      setError(getApiErrorMessage(requestError, '조직 자료실을 불러오지 못했습니다.'))
    } finally {
      if (version === requestRef.current) setLoading(false)
    }
  }, [
    activeView, apiRoot, currentFolderId, debouncedSearch, page,
    selectedRootOrgId, sortValue, tagFilter, typeFilter,
  ])

  useEffect(() => { loadWorkspaces() }, [loadWorkspaces])
  useEffect(() => { loadWorkspaceSupport() }, [loadWorkspaceSupport])
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 250)
    return () => clearTimeout(timer)
  }, [search])
  useEffect(() => { loadData() }, [loadData])
  useEffect(() => {
    if (!deepLinkFileId || deepLinkHandledRef.current || !selectedRootOrgId || !options.length) return
    if (deepLinkRootOrgId && String(selectedRootOrgId) !== String(deepLinkRootOrgId)) {
      const targetExists = options.some(
        (option) => String(option.rootOrgId) === String(deepLinkRootOrgId),
      )
      if (targetExists) return
      deepLinkHandledRef.current = true
      show(
        '\uC54C\uB9BC\uC774 \uC9C0\uC815\uD55C \uC870\uC9C1 \uC790\uB8CC\uC2E4\uC5D0 \uC811\uADFC\uD560 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4.',
        'error',
      )
      return
    }

    deepLinkHandledRef.current = true
    let cancelled = false
    const openDeepLinkedFile = async () => {
      try {
        const response = await api.get(
          `${apiRoot}/workspaces/${selectedRootOrgId}/files/${deepLinkFileId}/location`,
          { __silent: true, __suppressErrorPageRedirect: true },
        )
        if (cancelled) return
        const location = unwrap(response, null)
        if (!location) throw new Error()
        setActiveView(location.trashed ? 'TRASH' : 'ALL')
        setCurrentFolderId(location.trashed ? null : (location.folderId ?? null))
        setPage(0)
        setSearch('')
        setCollaborationFile({ id: location.fileId, name: location.fileName })
      } catch (requestError) {
        if (!cancelled) {
          show(getApiErrorMessage(requestError, '\uC54C\uB9BC \uB300\uC0C1 \uC790\uB8CC\uB97C \uC5F4 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4.'), 'error')
        }
      }
    }
    openDeepLinkedFile()
    return () => { cancelled = true }
  }, [apiRoot, deepLinkFileId, deepLinkRootOrgId, options, selectedRootOrgId, show])
  useEffect(() => { setSelectedItems(new Set()) }, [activeView, currentFolderId, page, selectedRootOrgId])
  useEffect(() => () => {
    if (preview?.url) URL.revokeObjectURL(preview.url)
  }, [preview])
  useEffect(() => {
    if (!contextMenu) return undefined
    const close = () => setContextMenu(null)
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') close()
    }
    window.addEventListener('click', close)
    window.addEventListener('resize', close)
    window.addEventListener('scroll', close, true)
    window.addEventListener('keydown', closeOnEscape)
    return () => {
      window.removeEventListener('click', close)
      window.removeEventListener('resize', close)
      window.removeEventListener('scroll', close, true)
      window.removeEventListener('keydown', closeOnEscape)
    }
  }, [contextMenu])

  const resetLocation = (view = 'ALL') => {
    setActiveView(view)
    setCurrentFolderId(null)
    setPage(0)
    setSearch('')
    setSelectedItems(new Set())
  }

  const mutate = async (request, successMessage) => {
    setBusy(true)
    try {
      await request()
      if (successMessage) show(successMessage, 'success')
      setSelectedItems(new Set())
      await loadData()
      await loadWorkspaces()
      await loadWorkspaceSupport()
      return true
    } catch (requestError) {
      show(getApiErrorMessage(requestError, '작업을 완료하지 못했습니다.'), 'error')
      return false
    } finally {
      setBusy(false)
    }
  }

  const openFolder = (folder) => {
    if (folder.trashed) return
    setActiveView('ALL')
    setCurrentFolderId(folder.id)
    setPage(0)
    setSearch('')
  }

  const submitEdit = async (event) => {
    event.preventDefault()
    const name = editValue.trim()
    if (!name || !editDialog) return
    const succeeded = await mutate(async () => {
      if (editDialog.mode === 'create') {
        await api.post(`${apiRoot}/workspaces/${selectedRootOrgId}/folders`, {
          name, parentFolderId: activeView === 'ALL' ? currentFolderId : null,
        })
      } else {
        const segment = editDialog.kind === 'folder' ? 'folders' : 'files'
        await api.patch(`${apiRoot}/workspaces/${selectedRootOrgId}/${segment}/${editDialog.item.id}`, { name })
      }
    }, editDialog.mode === 'create' ? '폴더를 만들었습니다.' : '이름을 변경했습니다.')
    if (succeeded) {
      setEditDialog(null)
      setEditValue('')
    }
  }

  const patchItem = (kind, item, body) => mutate(async () => {
    const segment = kind === 'folder' ? 'folders' : 'files'
    await api.patch(`${apiRoot}/workspaces/${selectedRootOrgId}/${segment}/${item.id}`, body)
  }, body.starred ? '중요 자료로 표시했습니다.' : '중요 표시를 해제했습니다.')

  const trashItem = (kind, item) => mutate(async () => {
    const segment = kind === 'folder' ? 'folders' : 'files'
    await api.delete(`${apiRoot}/workspaces/${selectedRootOrgId}/${segment}/${item.id}`)
  }, '휴지통으로 이동했습니다.')

  const restoreItem = (kind, item) => mutate(async () => {
    const segment = kind === 'folder' ? 'folders' : 'files'
    await api.post(`${apiRoot}/workspaces/${selectedRootOrgId}/${segment}/${item.id}/restore`)
  }, '자료를 복원했습니다.')

  const permanentlyDelete = async (kind, item) => {
    if (!window.confirm(`'${item.name}'을(를) 영구 삭제할까요? 이 작업은 되돌릴 수 없습니다.`)) return
    await mutate(async () => {
      const segment = kind === 'folder' ? 'folders' : 'files'
      await api.delete(`${apiRoot}/workspaces/${selectedRootOrgId}/${segment}/${item.id}/permanent`)
    }, '자료를 영구 삭제했습니다.')
  }

  const emptyTrash = async () => {
    if (!window.confirm('휴지통을 비울까요? 모든 항목이 영구 삭제되고 저장 용량이 회수됩니다.')) return
    await mutate(
      () => api.delete(`${apiRoot}/workspaces/${selectedRootOrgId}/trash`),
      '휴지통을 비웠습니다.',
    )
  }

  const submitMove = async () => {
    if (!moveDialog) return
    const targetFolderId = moveTarget ? Number(moveTarget) : null
    const succeeded = await mutate(async () => {
      if (moveDialog.bulk) {
        await api.post(`${apiRoot}/workspaces/${selectedRootOrgId}/bulk-actions`, {
          action: 'MOVE', items: selectedRefs(), targetFolderId,
        })
      } else {
        const segment = moveDialog.kind === 'folder' ? 'folders' : 'files'
        await api.post(
          `${apiRoot}/workspaces/${selectedRootOrgId}/${segment}/${moveDialog.item.id}/move`,
          { targetFolderId },
        )
      }
    }, '자료를 이동했습니다.')
    if (succeeded) setMoveDialog(null)
  }

  const selectedRefs = () => Array.from(selectedItems).map((key) => {
    const [kind, id] = key.split(':')
    return { kind: kind.toUpperCase(), id: Number(id) }
  })

  const openContextMenu = (event, kind, item) => {
    event.preventDefault()
    event.stopPropagation()
    if (activeView === 'TRASH') return
    const anchor = event.currentTarget?.getBoundingClientRect()
    const clientX = event.clientX || anchor?.left || 8
    const clientY = event.clientY || anchor?.bottom || 8
    setContextMenu({
      x: Math.max(8, Math.min(clientX, window.innerWidth - 230)),
      y: Math.max(8, Math.min(clientY, window.innerHeight - 360)),
      kind,
      item,
    })
  }

  const transferTypes = (event) => Array.from(event.dataTransfer?.types || [])
    .map((type) => String(type).toLowerCase())
  const isNativeFileDrag = (event) => transferTypes(event).includes('files')
  const isLibraryItemDrag = (event) => transferTypes(event).includes(ORG_LIBRARY_ITEMS_MIME)
  const folderIsWritable = (folder) => {
    if (readOnly) return false
    const treeFolder = folderTreeItems.find(
      (candidate) => String(candidate.id) === String(folder.id),
    )
    return treeFolder ? treeFolder.writable !== false : folder.manageable !== false
  }

  const startItemDrag = (event, kind, item) => {
    const key = itemKey(kind, item.id)
    const refs = selectedItems.has(key)
      ? selectedRefs()
      : [{ kind: kind.toUpperCase(), id: item.id }]
    const serialized = JSON.stringify(refs)
    event.dataTransfer.effectAllowed = 'move'
    event.dataTransfer.setData(ORG_LIBRARY_ITEMS_MIME, serialized)
    event.dataTransfer.setData('text/plain', serialized)
  }

  const moveRefsToFolder = async (refs, targetFolderId) => {
    if (!Array.isArray(refs) || !refs.length || readOnly) return
    await mutate(
      () => api.post(`${apiRoot}/workspaces/${selectedRootOrgId}/bulk-actions`, {
        action: 'MOVE',
        items: refs,
        targetFolderId,
      }),
      '자료를 이동했습니다.',
    )
  }


  const bulkAction = async (action) => {
    if (!selectedItems.size) return
    if (action === 'DELETE_PERMANENTLY'
      && !window.confirm(`선택한 ${selectedItems.size}개 항목을 영구 삭제할까요?`)) return
    await mutate(async () => {
      await api.post(`${apiRoot}/workspaces/${selectedRootOrgId}/bulk-actions`, {
        action, items: selectedRefs(), targetFolderId: null,
      })
    }, action === 'RESTORE' ? '선택한 자료를 복원했습니다.'
      : action === 'DELETE_PERMANENTLY' ? '선택한 자료를 영구 삭제했습니다.'
        : '선택한 자료를 휴지통으로 이동했습니다.')
  }

  const toggleSelection = (kind, id) => {
    const key = itemKey(kind, id)
    setSelectedItems((previous) => {
      const next = new Set(previous)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }
  const copyItem = (kind, item) => mutate(async () => {
    const segment = kind === 'folder' ? 'folders' : 'files'
    await api.post(`${apiRoot}/workspaces/${selectedRootOrgId}/${segment}/${item.id}/copy`, {
      targetFolderId: activeView === 'ALL' && canManageCurrentFolder
        ? currentFolderId : null,
    })
  }, '사본을 만들었습니다.')

  const waitForAsyncOperation = async (operationKey) => {
    for (let attempt = 0; attempt < 180; attempt += 1) {
      const response = await api.get(`/async-operations/${operationKey}`, {
        __silent: true, __suppressErrorPageRedirect: true,
      })
      const operation = unwrap(response, null)
      if (operation?.status === 'SUCCEEDED') return operation
      if (operation?.status === 'FAILED') throw new Error(operation.errorMessage || '')
      await sleep(1000)
    }
    throw new Error()
  }


  const bulkDownload = async () => {
    if (!selectedItems.size) return
    setBusy(true)
    try {
      const jobResponse = await api.post(
        `${apiRoot}/workspaces/${selectedRootOrgId}/bulk-download-jobs`,
        { items: selectedRefs() },
        { __silent: true, __suppressErrorPageRedirect: true },
      )
      const job = unwrap(jobResponse, null)
      if (!job?.operationKey) throw new Error()
      await waitForAsyncOperation(job.operationKey)
      const response = await api.get(
        `/async-operations/${job.operationKey}/download`,
        { responseType: 'blob', __silent: true, __suppressErrorPageRedirect: true },
      )
      const url = URL.createObjectURL(response.data)
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = '조직자료실_다운로드.zip'
      document.body.appendChild(anchor)
      anchor.click()
      anchor.remove()
      URL.revokeObjectURL(url)
    } catch (requestError) {
      show(getApiErrorMessage(requestError, '묶음 다운로드를 준비하지 못했습니다.'), 'error')
    } finally {
      setBusy(false)
    }
  }

  const openVersions = async (item) => {
    setVersionDialog(item)
    setVersions([])
    setVersionsLoading(true)
    try {
      const response = await api.get(
        `${apiRoot}/workspaces/${selectedRootOrgId}/files/${item.id}/versions`,
        { __silent: true, __suppressErrorPageRedirect: true },
      )
      setVersions(unwrap(response, []))
    } catch (requestError) {
      show(getApiErrorMessage(requestError, '파일 버전을 불러오지 못했습니다.'), 'error')
      setVersionDialog(null)
    } finally {
      setVersionsLoading(false)
    }
  }

  const uploadVersion = async (file) => {
    if (!file || !versionDialog) return
    const formData = new FormData()
    formData.append('file', file)
    const succeeded = await mutate(
      () => api.post(
        `${apiRoot}/workspaces/${selectedRootOrgId}/files/${versionDialog.id}/versions`,
        formData,
      ),
      '새 파일 버전을 등록했습니다.',
    )
    if (succeeded) await openVersions(versionDialog)
  }

  const downloadVersion = async (version) => {
    if (!versionDialog || version.current) {
      if (versionDialog) await downloadFile(versionDialog)
      return
    }
    setBusy(true)
    try {
      const response = await api.get(
        `${apiRoot}/workspaces/${selectedRootOrgId}/files/${versionDialog.id}/versions/${version.id}/content`,
        { responseType: 'blob', __silent: true, __suppressErrorPageRedirect: true },
      )
      const url = URL.createObjectURL(response.data)
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = versionDialog.name
      document.body.appendChild(anchor)
      anchor.click()
      anchor.remove()
      URL.revokeObjectURL(url)
    } catch (requestError) {
      show(getApiErrorMessage(requestError, '파일 버전을 다운로드하지 못했습니다.'), 'error')
    } finally {
      setBusy(false)
    }
  }

  const restoreVersion = async (version) => {
    if (!versionDialog || version.current) return
    const succeeded = await mutate(
      () => api.post(`${apiRoot}/workspaces/${selectedRootOrgId}/files/${versionDialog.id}/versions/${version.id}/restore`),
      '선택한 버전을 현재 버전으로 복원했습니다.',
    )
    if (succeeded) await openVersions(versionDialog)
  }


  const toggleAll = () => {
    setSelectedItems((previous) => (
      previous.size === items.length
        ? new Set()
        : new Set(items.map(({ kind, item }) => itemKey(kind, item.id)))
    ))
  }

  const uploadSingle = async (file, folderId, uploadRequestId, onProgress) => {
    if (uploadCapabilities?.uploadReady === false) {
      throw new Error(
        uploadCapabilities.unavailableReason || '\uD30C\uC77C \uC548\uC804 \uAC80\uC0AC\uB97C \uC0AC\uC6A9\uD560 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4.',
      )
    }
    const fallbackMax = Number(
      uploadCapabilities?.fallbackMaxUploadBytes
        || workspace?.maxUploadBytes
        || 10 * 1024 * 1024,
    )
    const maxUploadBytes = Number(uploadCapabilities?.maxUploadBytes || fallbackMax)
    if (file.size > maxUploadBytes) {
      throw new Error(`${file.name}: 파일 크기는 ${formatBytes(maxUploadBytes)} 이하여야 합니다.`)
    }
    const direct = Boolean(uploadCapabilities?.directUploadEnabled) && file.size > fallbackMax
    if (direct) {
      const beginResponse = await api.post(
        `${apiRoot}/workspaces/${selectedRootOrgId}/upload-sessions`,
        {
          folderId,
          uploadRequestId,
          fileName: file.name,
          contentType: file.type || 'application/octet-stream',
          sizeBytes: file.size,
          sha256: null,
        },
        { __silent: true, __suppressErrorPageRedirect: true },
      )
      const session = unwrap(beginResponse, null)
      if (!session?.sessionKey || !session?.uploadUrl) throw new Error()
      await putDirectFile(session, file, onProgress)
      const completeResponse = await api.post(
        `${apiRoot}/workspaces/${selectedRootOrgId}/upload-sessions/${session.sessionKey}/complete`,
        null,
        { __silent: true, __suppressErrorPageRedirect: true },
      )
      const operation = unwrap(completeResponse, null)
      if (operation?.operationKey) await waitForAsyncOperation(operation.operationKey)
      return
    }
    if (file.size > fallbackMax) {
      throw new Error(`${file.name}: 현재 파일 크기는 ${formatBytes(fallbackMax)} 이하여야 합니다.`)
    }
    const formData = new FormData()
    formData.append('file', file)
    if (folderId != null) formData.append('folderId', folderId)
    if (uploadRequestId != null) formData.append('uploadRequestId', uploadRequestId)
    await api.post(
      `${apiRoot}/workspaces/${selectedRootOrgId}/files`,
      formData,
      {
        __silent: true,
        __suppressErrorPageRedirect: true,
        onUploadProgress: (event) => {
          if (event.total) onProgress?.(Math.round((event.loaded / event.total) * 100))
        },
      },
    )
  }

  const performUploads = async (entries) => {
    let succeeded = 0
    const failed = []
    for (let index = 0; index < entries.length; index += 1) {
      const entry = entries[index]
      setUploadProgress({ fileName: entry.file.name, index: index + 1, total: entries.length, percent: 0 })
      try {
        await uploadSingle(
          entry.file,
          entry.folderId,
          entry.uploadRequestId,
          (percent) => setUploadProgress({
            fileName: entry.file.name,
            index: index + 1,
            total: entries.length,
            percent,
          }),
        )
        succeeded += 1
      } catch (requestError) {
        failed.push({
          ...entry,
          errorMessage: getApiErrorMessage(requestError, '\uD30C\uC77C\uC744 \uC5C5\uB85C\uB4DC\uD558\uC9C0 \uBABB\uD588\uC2B5\uB2C8\uB2E4.'),
        })
      }
    }
    return { succeeded, failed }
  }

  const uploadFiles = async (fileList, options = {}) => {
    const selectedFiles = Array.from(fileList || [])
    const targetWritable = options.targetWritable ?? canManageCurrentFolder
    if (!selectedFiles.length || readOnly || (!options.uploadRequestId && !targetWritable)) return
    const folderId = options.folderId !== undefined ? options.folderId : currentFolderId
    setBusy(true)
    try {
      const result = await performUploads(selectedFiles.map((file) => ({
        file,
        folderId,
        uploadRequestId: options.uploadRequestId || null,
      })))
      if (result.failed.length) {
        setFailedUploads((previous) => [...previous, ...result.failed])
        if (result.succeeded > 0) {
          await Promise.all([loadData(), loadWorkspaces(), loadWorkspaceSupport()])
        }
        show(String(result.failed.length) + '\uAC1C \uD30C\uC77C \uC5C5\uB85C\uB4DC\uB97C \uC644\uB8CC\uD558\uC9C0 \uBABB\uD588\uC2B5\uB2C8\uB2E4.', 'error')
        return
      }
      show(`${selectedFiles.length}개 파일을 업로드했습니다.`, 'success')
      setPendingUploadRequest(null)
      if (options.uploadRequestId) {
        setActiveView('ALL')
        setCurrentFolderId(folderId)
      }
      await Promise.all([loadData(), loadWorkspaces(), loadWorkspaceSupport()])
    } catch (requestError) {
      show(getApiErrorMessage(requestError, '파일을 업로드하지 못했습니다.'), 'error')
    } finally {
      setUploadProgress(null)
      setBusy(false)
    }
  }
  const retryFailedUploads = async () => {
    if (busy || !failedUploads.length) return
    const pending = failedUploads
    setFailedUploads([])
    setBusy(true)
    try {
      const result = await performUploads(pending)
      setFailedUploads(result.failed)
      if (result.succeeded > 0) {
        await Promise.all([loadData(), loadWorkspaces(), loadWorkspaceSupport()])
      }
      if (result.failed.length) {
        show(String(result.failed.length) + '\uAC1C \uD30C\uC77C\uC774 \uC5EC\uC804\uD788 \uC5C5\uB85C\uB4DC\uB418\uC9C0 \uC54A\uC558\uC2B5\uB2C8\uB2E4.', 'error')
      } else {
        show(String(result.succeeded) + '\uAC1C \uD30C\uC77C\uC744 \uC5C5\uB85C\uB4DC\uD588\uC2B5\uB2C8\uB2E4.', 'success')
      }
    } finally {
      setUploadProgress(null)
      setBusy(false)
    }
  }

  const handleFolderDragOver = (event, folder) => {
    const files = isNativeFileDrag(event)
    const libraryItems = isLibraryItemDrag(event)
    if (!files && !libraryItems) return
    event.preventDefault()
    event.stopPropagation()
    setDropActive(false)
    const writable = folderIsWritable(folder)
    event.dataTransfer.dropEffect = writable ? (files ? 'copy' : 'move') : 'none'
    setDropTargetFolderId(writable ? folder.id : null)
  }

  const handleFolderDragLeave = (event, folder) => {
    if (!event.currentTarget.contains(event.relatedTarget)
      && String(dropTargetFolderId) === String(folder.id)) {
      setDropTargetFolderId(null)
    }
  }

  const handleFolderDrop = (event, folder) => {
    const files = Array.from(event.dataTransfer?.files || [])
    const raw = event.dataTransfer.getData(ORG_LIBRARY_ITEMS_MIME)
      || (isLibraryItemDrag(event) ? event.dataTransfer.getData('text/plain') : '')
    if (!files.length && !raw) return
    event.preventDefault()
    event.stopPropagation()
    setDropActive(false)
    setDropTargetFolderId(null)
    if (!folderIsWritable(folder)) {
      show('이 폴더에 자료를 추가할 권한이 없습니다.', 'error')
      return
    }
    if (files.length) {
      uploadFiles(files, { folderId: folder.id, targetWritable: true })
      return
    }
    try {
      const refs = JSON.parse(raw)
      if (!Array.isArray(refs) || !refs.length) return
      const movableRefs = refs.filter(
        (ref) => !(ref.kind === 'FOLDER' && String(ref.id) === String(folder.id)),
      )
      if (!movableRefs.length) {
        show('폴더를 자기 자신으로 이동할 수 없습니다.', 'error')
        return
      }
      moveRefsToFolder(movableRefs, folder.id)
    } catch {
      show('자료를 이동하지 못했습니다.', 'error')
    }
  }

  const uploadFolder = async (fileList) => {
    const selectedFiles = Array.from(fileList || []).filter((file) => file.webkitRelativePath)
    if (!selectedFiles.length || readOnly || !canManageCurrentFolder) return
    setBusy(true)
    try {
      const folderIds = new Map([['', currentFolderId]])
      const existing = new Map(
        folderTreeItems.map((folder) => [
          `${folder.parentFolderId ?? 'root'}:${folder.name.toLocaleLowerCase('ko-KR')}`,
          folder.id,
        ]),
      )
      const paths = [...new Set(selectedFiles
        .map((file) => file.webkitRelativePath.split('/').slice(0, -1).join('/'))
        .filter(Boolean))]
        .sort((left, right) => left.split('/').length - right.split('/').length)
      for (const path of paths) {
        const parts = path.split('/')
        const name = parts.at(-1)
        const parentPath = parts.slice(0, -1).join('/')
        const parentFolderId = folderIds.get(parentPath) ?? currentFolderId
        const existingId = existing.get(
          `${parentFolderId ?? 'root'}:${name.toLocaleLowerCase('ko-KR')}`,
        )
        if (existingId) {
          folderIds.set(path, existingId)
          continue
        }
        const response = await api.post(
          `${apiRoot}/workspaces/${selectedRootOrgId}/folders`,
          { name, parentFolderId },
        )
        const created = unwrap(response, null)
        folderIds.set(path, created.id)
        existing.set(
          `${parentFolderId ?? 'root'}:${name.toLocaleLowerCase('ko-KR')}`,
          created.id,
        )
      }
      const result = await performUploads(selectedFiles.map((file) => {
        const relativeFolder = file.webkitRelativePath.split('/').slice(0, -1).join('/')
        return { file, folderId: folderIds.get(relativeFolder) ?? currentFolderId, uploadRequestId: null }
      }))
      if (result.failed.length) {
        setFailedUploads((previous) => [...previous, ...result.failed])
        await Promise.all([loadData(), loadWorkspaces(), loadWorkspaceSupport()])
        show(String(result.failed.length) + '\uAC1C \uD30C\uC77C \uC5C5\uB85C\uB4DC\uB97C \uC644\uB8CC\uD558\uC9C0 \uBABB\uD588\uC2B5\uB2C8\uB2E4.', 'error')
        return
      }
      show(`${selectedFiles.length}개 파일을 폴더 구조와 함께 업로드했습니다.`, 'success')
      await Promise.all([loadData(), loadWorkspaces(), loadWorkspaceSupport()])
    } catch (requestError) {
      show(getApiErrorMessage(requestError, '폴더를 업로드하지 못했습니다.'), 'error')
    } finally {
      setUploadProgress(null)
      setBusy(false)
    }
  }

  const fetchBlob = async (item, download) => {
    const response = await api.get(
      `${apiRoot}/workspaces/${selectedRootOrgId}/files/${item.id}/content`,
      { params: { download }, responseType: 'blob', __silent: true, __suppressErrorPageRedirect: true },
    )
    return response.data
  }

  const previewFile = async (item) => {
    if (!fileContentAvailable(item)) {
      show(scanUnavailableMessage(item), 'error')
      return
    }
    setBusy(true)
    try {
      const blob = await fetchBlob(item, false)
      const url = URL.createObjectURL(blob)
      setPreview((previous) => {
        if (previous?.url) URL.revokeObjectURL(previous.url)
        return { item, url, type: blob.type }
      })
    } catch (requestError) {
      show(getApiErrorMessage(requestError, '미리보기를 열지 못했습니다.'), 'error')
    } finally {
      setBusy(false)
    }
  }

  const downloadFile = async (item) => {
    if (!fileContentAvailable(item)) {
      show(scanUnavailableMessage(item), 'error')
      return
    }
    setBusy(true)
    try {
      const blob = await fetchBlob(item, true)
      const url = URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = item.name
      document.body.appendChild(anchor)
      anchor.click()
      anchor.remove()
      URL.revokeObjectURL(url)
    } catch (requestError) {
      show(getApiErrorMessage(requestError, '파일을 다운로드하지 못했습니다.'), 'error')
    } finally {
      setBusy(false)
    }
  }

  const moveDestinations = useMemo(() => {
    const values = new Map()
    for (const breadcrumb of contents?.breadcrumbs || []) {
      if (breadcrumb.folderId != null) values.set(String(breadcrumb.folderId), breadcrumb.name)
    }
    for (const folder of folders) {
      if (!folder.trashed
        && folder.manageable !== false
        && (!moveDialog?.item || folder.id !== moveDialog.item.id)) {
        values.set(String(folder.id), folder.name)
      }
    }
    return Array.from(values, ([id, name]) => ({ id, name }))
  }, [contents?.breadcrumbs, folders, moveDialog?.item])

  const hasItems = items.length > 0
  const totalPages = Math.max(1, Math.ceil(Number(contents?.totalItems || activities?.totalItems || 0) / (activeView === 'ACTIVITY' ? 30 : PAGE_SIZE)))
  const selectedRows = items.filter(({ kind, item }) => selectedItems.has(itemKey(kind, item.id)))
  const selectedManageable = selectedRows.every(({ item }) => item.manageable !== false)
  const selectedContentUnavailable = selectedRows.some(
    ({ kind, item }) => kind === 'file' && !fileContentAvailable(item),
  )


  return (
    <div className="mx-auto w-full max-w-[1680px] px-3 py-4 sm:px-5 lg:px-7">
      <header className="mb-4 flex flex-col gap-3 border-b border-slate-200 pb-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="min-w-0">
          <h1 className="text-xl font-semibold text-slate-950">조직 자료실</h1>
          {workspace && (
            <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-500">
              <span className="inline-flex items-center gap-1"><Building2 className="h-4 w-4" />{workspace.includedOrgCount || 1}개 조직</span>
              <span className="inline-flex items-center gap-1"><Users className="h-4 w-4" />{workspace.memberCount || 0}명</span>
              {readOnly && <span className="font-medium text-amber-700">계약 종료 · 읽기 전용</span>}
            </div>
          )}
        </div>
        <div className="flex w-full flex-col gap-2 sm:flex-row lg:w-auto">
          <select
            aria-label="계약 조직 선택"
            value={selectedRootOrgId}
            onChange={(event) => {
              setSelectedRootOrgId(event.target.value)
              resetLocation('ALL')
            }}
            className="h-10 min-w-0 rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-800 sm:min-w-[260px]"
          >
            {!options.length && <option value="">조직 자료실 없음</option>}
            {options.map((option) => (
              <option key={option.rootOrgId} value={option.rootOrgId}>
                {option.orgName}{option.readOnly ? ' (읽기 전용)' : ''}
              </option>
            ))}
          </select>
          <div className="relative">
            <button
              type="button"
              aria-label="새로 만들기"
              disabled={!selectedRootOrgId || readOnly || !canManageCurrentFolder || SPECIAL_VIEWS.has(activeView)}
              onClick={() => setNewMenuOpen((open) => !open)}
              className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-md bg-sgez-primary px-4 text-sm font-semibold text-white hover:bg-sgez-primary/90 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
            >
              <Plus className="h-4 w-4" />새로 만들기
            </button>
            {newMenuOpen && (
              <div className="absolute right-0 z-30 mt-1 w-44 rounded-md border border-slate-200 bg-white p-1 shadow-lg">
                <button
                  type="button"
                  onClick={() => {
                    setNewMenuOpen(false)
                    setEditDialog({ mode: 'create' })
                    setEditValue('')
                  }}
                  className="flex w-full items-center gap-2 rounded px-3 py-2 text-sm hover:bg-slate-100"
                >
                  <FolderPlus className="h-4 w-4" />새 폴더
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setNewMenuOpen(false)
                    setPendingUploadRequest(null)
                    uploadInputRef.current?.click()
                  }}
                  className="flex w-full items-center gap-2 rounded px-3 py-2 text-sm hover:bg-slate-100"
                >
                  <Upload className="h-4 w-4" />파일 업로드
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setNewMenuOpen(false)
                    folderUploadInputRef.current?.click()
                  }}
                  className="flex w-full items-center gap-2 rounded px-3 py-2 text-sm hover:bg-slate-100"
                >
                  <FolderUp className="h-4 w-4" />폴더 업로드
                </button>
              </div>
            )}
          </div>
          <input
            ref={uploadInputRef}
            type="file"
            accept={ACCEPTED_FILE_TYPES}
            multiple
            className="hidden"
            onChange={(event) => {
              uploadFiles(event.target.files, pendingUploadRequest
                ? { folderId: pendingUploadRequest.folderId, uploadRequestId: pendingUploadRequest.id }
                : {})
              event.target.value = ''
            }}
          />
          <input
            ref={folderUploadInputRef}
            type="file"
            multiple
            webkitdirectory=""
            className="hidden"
            onChange={(event) => {
              uploadFolder(event.target.files)
              event.target.value = ''
            }}
          />
        </div>
      </header>

      {uploadProgress && (
        <div role="status" className="mb-3 border border-cyan-200 bg-cyan-50 px-3 py-2">
          <div className="flex items-center justify-between gap-3 text-sm">
            <span className="min-w-0 truncate font-medium text-cyan-900">
              업로드 중 {uploadProgress.index}/{uploadProgress.total} · {uploadProgress.fileName}
            </span>
            <span className="shrink-0 text-cyan-700">{uploadProgress.percent}%</span>
          </div>
          <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-cyan-100">
            <div className="h-full bg-cyan-600" style={{ width: `${uploadProgress.percent}%` }} />
          </div>
        </div>
      )}


      {failedUploads.length > 0 && (
        <div role="alert" className="mb-3 flex flex-wrap items-center justify-between gap-3 border border-amber-200 bg-amber-50 px-3 py-2">
          <div className="flex min-w-0 items-center gap-2">
            <UploadCloud className="h-5 w-5 shrink-0 text-amber-700" />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-amber-950">
                {failedUploads.length}{'\uAC1C \uD30C\uC77C \uC5C5\uB85C\uB4DC \uBBF8\uC644\uB8CC'}
              </p>
              <p
                className="truncate text-xs text-amber-800"
                title={failedUploads
                  .map((entry) => entry.file.name + ': ' + entry.errorMessage)
                  .join('\n')}
              >
                {failedUploads.map((entry) => entry.file.name).join(', ')}
              </p>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <button
              type="button"
              disabled={busy}
              onClick={retryFailedUploads}
              className="inline-flex h-8 items-center gap-1 rounded-md border border-amber-300 bg-white px-3 text-sm font-medium text-amber-900 disabled:opacity-50"
            >
              <RefreshCw className="h-4 w-4" />{'\uC7AC\uC2DC\uB3C4'}
            </button>
            <IconButton title={'\uBAA9\uB85D \uC9C0\uC6B0\uAE30'} disabled={busy} onClick={() => setFailedUploads([])}><X className="h-4 w-4" /></IconButton>
          </div>
        </div>
      )}
      <div className="grid min-h-[650px] grid-cols-1 border border-slate-200 bg-white lg:grid-cols-[250px_minmax(0,1fr)]">
        <aside className="border-b border-slate-200 p-3 lg:border-b-0 lg:border-r">
          <nav className="grid grid-cols-2 gap-1 sm:grid-cols-4 lg:grid-cols-1" aria-label="자료실 보기">
            {libraryViews.map(({ key, label, icon: ViewIcon }) => (
              <button
                key={key}
                type="button"
                onClick={() => resetLocation(key)}
                className={`flex h-10 items-center gap-2 rounded-md px-3 text-sm font-medium ${activeView === key ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
              >
                <ViewIcon className="h-4 w-4" />{label}
              </button>
            ))}
          </nav>
          {!SPECIAL_VIEWS.has(activeView) && activeView !== 'TRASH' && (
            <OrgLibraryFolderTree
              items={folderTreeItems}
              currentFolderId={currentFolderId}
              rootLabel={workspace?.orgName}
              disabled={readOnly}
              onSelect={(folderId) => {
                setActiveView('ALL')
                setCurrentFolderId(folderId)
                setPage(0)
                setSearch('')
              }}
              onDropItems={moveRefsToFolder}
              onDropFiles={(files, folderId) => uploadFiles(files, { folderId, targetWritable: true })}
            />
          )}
          <div className="mt-5 border-t border-slate-200 pt-4">
            <div className="flex items-center justify-between text-xs text-slate-500">
              <span className="inline-flex items-center gap-1"><HardDrive className="h-3.5 w-3.5" />저장 용량</span>
              <span>{Math.round(usagePercent)}%</span>
            </div>
            <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-slate-200">
              <div className="h-full bg-cyan-600" style={{ width: `${usagePercent}%` }} />
            </div>
            <p className="mt-2 text-xs font-medium text-slate-700">{formatBytes(used)} / {formatBytes(quota)}</p>
            <p className="mt-1 text-xs text-slate-500">휴지통 파일도 사용량에 포함됩니다.</p>
          </div>
        </aside>

        <main
          className={`relative min-w-0 ${dropActive ? 'ring-2 ring-inset ring-cyan-500' : ''}`}
          onDragEnter={(event) => {
            if (!isNativeFileDrag(event)) return
            event.preventDefault()
            event.dataTransfer.dropEffect = canDropToCurrentFolder ? 'copy' : 'none'
            if (canDropToCurrentFolder) setDropActive(true)
          }}
          onDragOver={(event) => {
            if (!isNativeFileDrag(event)) return
            event.preventDefault()
            event.dataTransfer.dropEffect = canDropToCurrentFolder ? 'copy' : 'none'
            if (canDropToCurrentFolder) setDropActive(true)
          }}
          onDragLeave={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget)) setDropActive(false)
          }}
          onDrop={(event) => {
            if (!event.dataTransfer.files?.length) return
            event.preventDefault()
            event.stopPropagation()
            setDropActive(false)
            if (!canDropToCurrentFolder) {
              show('현재 화면에는 파일을 업로드할 수 없습니다.', 'error')
              return
            }
            uploadFiles(event.dataTransfer.files)
          }}
        >
          {dropActive && (
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-3 z-30 flex items-center justify-center rounded-md border-2 border-dashed border-cyan-500 bg-white/90"
            >
              <div className="flex items-center gap-2 text-sm font-semibold text-cyan-800">
                <UploadCloud className="h-5 w-5" />
                파일을 놓아 업로드
              </div>
            </div>
          )}
          <div className="flex flex-col gap-3 border-b border-slate-200 p-3 sm:p-4">
            <div className="flex flex-col gap-2 xl:flex-row xl:items-center xl:justify-between">
              <div className="flex min-w-0 items-center gap-1 overflow-x-auto">
                {activeView === 'ALL' ? (contents?.breadcrumbs || []).map((breadcrumb, index) => (
                  <React.Fragment key={breadcrumb.folderId ?? 'root'}>
                    {index > 0 && <ChevronRight className="h-4 w-4 shrink-0 text-slate-400" />}
                    <button
                      type="button"
                      onClick={() => {
                        setCurrentFolderId(breadcrumb.folderId)
                        setPage(0)
                        setSearch('')
                      }}
                      className="max-w-[220px] truncate rounded px-1 py-1 text-sm font-medium text-slate-700 hover:bg-slate-100"
                    >
                      {breadcrumb.name}
                    </button>
                  </React.Fragment>
                )) : (
                  <h2 className="text-base font-semibold text-slate-900">
                    {libraryViews.find((view) => view.key === activeView)?.label}
                  </h2>
                )}
              </div>
              <div className="flex items-center gap-2">
                {!SPECIAL_VIEWS.has(activeView) && (
                  <label className="relative min-w-0 flex-1 sm:w-72 sm:flex-none">
                    <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                    <input
                      value={search}
                      onChange={(event) => {
                        setSearch(event.target.value)
                        setPage(0)
                      }}
                      placeholder="이름 검색"
                      className="h-9 w-full rounded-md border border-slate-300 pl-9 pr-3 text-sm"
                    />
                  </label>
                )}
                {activeView === 'TRASH' && items.length > 0 && canManageAll && (
                  <button
                    type="button"
                    onClick={emptyTrash}
                    disabled={busy}
                    className="h-9 shrink-0 rounded-md border border-red-200 px-3 text-sm font-medium text-red-700 hover:bg-red-50"
                  >
                    휴지통 비우기
                  </button>
                )}
                {!SPECIAL_VIEWS.has(activeView) && (
                  <div className="flex rounded-md border border-slate-300 p-0.5">
                    <IconButton title="목록 보기" onClick={() => setDisplayMode('list')} className={displayMode === 'list' ? 'bg-slate-100 text-slate-900' : ''}><List className="h-4 w-4" /></IconButton>
                    <IconButton title="격자 보기" onClick={() => setDisplayMode('grid')} className={displayMode === 'grid' ? 'bg-slate-100 text-slate-900' : ''}><LayoutGrid className="h-4 w-4" /></IconButton>
                  </div>
                )}
              </div>
            </div>

            {!SPECIAL_VIEWS.has(activeView) && (
              <div className="flex flex-wrap items-center gap-2">
                <SlidersHorizontal className="h-4 w-4 text-slate-400" />
                <select
                  aria-label="정렬"
                  value={sortValue}
                  onChange={(event) => { setSortValue(event.target.value); setPage(0) }}
                  className="h-9 rounded-md border border-slate-300 bg-white px-2 text-sm"
                >
                  {SORT_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </select>
                <select
                  aria-label="파일 형식"
                  value={typeFilter}
                  onChange={(event) => { setTypeFilter(event.target.value); setPage(0) }}
                  className="h-9 rounded-md border border-slate-300 bg-white px-2 text-sm"
                >
                  {TYPE_FILTERS.map(([value, label]) => <option key={value || 'ALL'} value={value}>{label}</option>)}
                </select>
                <input
                  value={tagFilter}
                  onChange={(event) => { setTagFilter(event.target.value); setPage(0) }}
                  placeholder="태그 필터"
                  className="h-9 min-w-0 flex-1 rounded-md border border-slate-300 px-3 text-sm sm:max-w-48"
                />
              </div>
            )}


            {selectedItems.size > 0 && !SPECIAL_VIEWS.has(activeView) && (
              <div className="flex flex-wrap items-center gap-2 bg-slate-50 px-3 py-2 text-sm">
                <span className="font-semibold text-slate-800">{selectedItems.size}개 선택</span>
                <button
                  type="button"
                  onClick={bulkDownload}
                  disabled={busy || selectedContentUnavailable}
                  title={selectedContentUnavailable ? '안전 검사가 완료된 파일만 다운로드할 수 있습니다.' : '묶음 다운로드'}
                  className="inline-flex items-center gap-1 rounded-md px-2 py-1.5 hover:bg-white disabled:cursor-not-allowed disabled:opacity-40"
                ><Download className="h-4 w-4" />묶음 다운로드</button>
                {activeView === 'TRASH' ? (
                  <>
                    {!readOnly && selectedManageable && <button type="button" onClick={() => bulkAction('RESTORE')} className="inline-flex items-center gap-1 rounded-md px-2 py-1.5 hover:bg-white"><RotateCcw className="h-4 w-4" />복원</button>}
                    {selectedManageable && <button type="button" onClick={() => bulkAction('DELETE_PERMANENTLY')} className="inline-flex items-center gap-1 rounded-md px-2 py-1.5 text-red-700 hover:bg-white"><Trash2 className="h-4 w-4" />영구 삭제</button>}
                  </>
                ) : !readOnly && selectedManageable && (
                  <>
                    <button type="button" onClick={() => setMoveDialog({ bulk: true })} className="inline-flex items-center gap-1 rounded-md px-2 py-1.5 hover:bg-white"><Move className="h-4 w-4" />이동</button>
                    <button type="button" onClick={() => bulkAction('TRASH')} className="inline-flex items-center gap-1 rounded-md px-2 py-1.5 text-red-700 hover:bg-white"><Trash2 className="h-4 w-4" />휴지통</button>
                  </>
                )}
                <IconButton title="선택 해제" onClick={() => setSelectedItems(new Set())}><X className="h-4 w-4" /></IconButton>
              </div>
            )}
          </div>

          <div className="min-h-[500px] p-3 sm:p-4">
            {loading ? (
              <div className="flex min-h-[360px] items-center justify-center"><LoaderCircle className="h-7 w-7 animate-spin text-slate-400" /></div>
            ) : error ? (
              <div className="flex min-h-[360px] flex-col items-center justify-center gap-3 text-sm text-slate-600">
                <p>{error}</p>
                <button type="button" onClick={loadData} className="rounded-md border border-slate-300 px-3 py-2">다시 시도</button>
              </div>
            ) : activeView === 'INSIGHTS' ? (
              <OrgLibraryInsightsPanel
                key={selectedRootOrgId}
                apiRoot={apiRoot}
                rootOrgId={selectedRootOrgId}
                canManage={canManageAll}
                isOperator={isOperator}
              />
            ) : activeView === 'REQUESTS' || activeView === 'REVIEWS' ? (
              <OrgLibraryWorkflowPanel
                key={`${selectedRootOrgId}:${activeView}`}
                mode={activeView}
                apiRoot={apiRoot}
                rootOrgId={selectedRootOrgId}
                folderTree={folderTreeItems}
                canManage={canManageAll}
                onUpload={(request) => {
                  setPendingUploadRequest(request)
                  uploadInputRef.current?.click()
                }}
                onOpenFile={setCollaborationFile}
              />
            ) : activeView === 'ACTIVITY' ? (
              <ActivityList activities={activities?.items || []} />
            ) : !hasItems ? (
              <div className="flex min-h-[360px] flex-col items-center justify-center text-center text-slate-500">
                {activeView === 'TRASH' ? <Trash2 className="mb-3 h-10 w-10 text-slate-300" /> : <FolderOpen className="mb-3 h-10 w-10 text-slate-300" />}
                <p className="text-sm">{debouncedSearch ? '검색 결과가 없습니다.' : activeView === 'TRASH' ? '휴지통이 비어 있습니다.' : '자료가 없습니다.'}</p>
              </div>
            ) : displayMode === 'grid' ? (
              <ItemGrid
                items={items}
                selected={selectedItems}
                readOnly={readOnly}
                activeView={activeView}
                busy={busy}
                onSelect={toggleSelection}
                onOpenFolder={openFolder}
                onPreview={previewFile}
                onDownload={downloadFile}
                onRename={(kind, item) => { setEditDialog({ mode: 'rename', kind, item }); setEditValue(item.name) }}
                onCopy={copyItem}
                onVersions={openVersions}
                onStar={patchItem}
                onMove={(kind, item) => { setMoveDialog({ kind, item }); setMoveTarget('') }}
                onTrash={trashItem}
                onRestore={restoreItem}
                onPermanentDelete={permanentlyDelete}
                onShareFolder={setPermissionFolder}
                onCollaboration={setCollaborationFile}
                onContextMenu={openContextMenu}
                onDragStart={startItemDrag}
                dropTargetFolderId={dropTargetFolderId}
                onFolderDragOver={handleFolderDragOver}
                onFolderDragLeave={handleFolderDragLeave}
                onFolderDrop={handleFolderDrop}
                onDragEnd={() => setDropTargetFolderId(null)}
              />
            ) : (
              <ItemTable
                items={items}
                selected={selectedItems}
                readOnly={readOnly}
                activeView={activeView}
                busy={busy}
                onSelect={toggleSelection}
                onSelectAll={toggleAll}
                onOpenFolder={openFolder}
                onPreview={previewFile}
                onDownload={downloadFile}
                onRename={(kind, item) => { setEditDialog({ mode: 'rename', kind, item }); setEditValue(item.name) }}
                onStar={patchItem}
                onCopy={copyItem}
                onVersions={openVersions}
                onMove={(kind, item) => { setMoveDialog({ kind, item }); setMoveTarget('') }}
                onTrash={trashItem}
                onRestore={restoreItem}
                onPermanentDelete={permanentlyDelete}
                onShareFolder={setPermissionFolder}
                onCollaboration={setCollaborationFile}
                onContextMenu={openContextMenu}
                onDragStart={startItemDrag}
                dropTargetFolderId={dropTargetFolderId}
                onFolderDragOver={handleFolderDragOver}
                onFolderDragLeave={handleFolderDragLeave}
                onFolderDrop={handleFolderDrop}
                onDragEnd={() => setDropTargetFolderId(null)}
              />
            )}
          </div>

          {!loading && !error && !SPECIAL_VIEWS.has(activeView) && totalPages > 1 && (
            <div className="flex items-center justify-center gap-3 border-t border-slate-200 px-4 py-3">
              <IconButton title="이전 페이지" disabled={page === 0} onClick={() => setPage((value) => Math.max(0, value - 1))}><ChevronLeft className="h-4 w-4" /></IconButton>
              <span className="text-sm text-slate-600">{page + 1} / {totalPages}</span>
              <IconButton title="다음 페이지" disabled={page + 1 >= totalPages} onClick={() => setPage((value) => value + 1)}><ChevronRight className="h-4 w-4" /></IconButton>
            </div>
          )}
        </main>
      </div>

      {editDialog && (
        <DialogBackdrop onMouseDown={(event) => event.target === event.currentTarget && !busy && setEditDialog(null)}>
          <DialogPanel aria-labelledby="org-library-edit-title">
            <form onSubmit={submitEdit}>
              <DialogHeader>
                <h2 id="org-library-edit-title" className="font-semibold text-slate-900">{editDialog.mode === 'create' ? '새 폴더' : '이름 변경'}</h2>
                <IconButton title="닫기" onClick={() => setEditDialog(null)}><X className="h-4 w-4" /></IconButton>
              </DialogHeader>
              <DialogBody>
                <label className="block text-sm font-medium text-slate-700">
                  이름
                  <input autoFocus value={editValue} onChange={(event) => setEditValue(event.target.value)} className="mt-2 h-10 w-full rounded-md border border-slate-300 px-3" />
                </label>
              </DialogBody>
              <DialogFooter>
                <button type="button" onClick={() => setEditDialog(null)} className="h-9 rounded-md border border-slate-300 px-4 text-sm">취소</button>
                <button type="submit" disabled={busy || !editValue.trim()} className="h-9 rounded-md bg-sgez-primary px-4 text-sm font-semibold text-white disabled:opacity-50">저장</button>
              </DialogFooter>
            </form>
          </DialogPanel>
        </DialogBackdrop>
      )}

      {moveDialog && (
        <DialogBackdrop onMouseDown={(event) => event.target === event.currentTarget && !busy && setMoveDialog(null)}>
          <DialogPanel aria-labelledby="org-library-move-title">
            <DialogHeader>
              <h2 id="org-library-move-title" className="font-semibold text-slate-900">이동할 위치</h2>
              <IconButton title="닫기" onClick={() => setMoveDialog(null)}><X className="h-4 w-4" /></IconButton>
            </DialogHeader>
            <DialogBody>
              <label className="block text-sm font-medium text-slate-700">
                대상 폴더
                <select value={moveTarget} onChange={(event) => setMoveTarget(event.target.value)} className="mt-2 h-10 w-full rounded-md border border-slate-300 px-3">
                  <option value="">최상위</option>
                  {moveDestinations.map((destination) => <option key={destination.id} value={destination.id}>{destination.name}</option>)}
                </select>
              </label>
            </DialogBody>
            <DialogFooter>
              <button type="button" onClick={() => setMoveDialog(null)} className="h-9 rounded-md border border-slate-300 px-4 text-sm">취소</button>
              <button type="button" onClick={submitMove} disabled={busy} className="h-9 rounded-md bg-sgez-primary px-4 text-sm font-semibold text-white disabled:opacity-50">이동</button>
            </DialogFooter>
          </DialogPanel>
        </DialogBackdrop>
      )}

      {versionDialog && (
        <DialogBackdrop onMouseDown={(event) => event.target === event.currentTarget && !busy && setVersionDialog(null)}>
          <DialogPanel aria-labelledby="org-library-version-title">
            <DialogHeader>
              <div className="min-w-0">
                <h2 id="org-library-version-title" className="font-semibold text-slate-900">파일 버전</h2>
                <p className="mt-0.5 truncate text-sm text-slate-500">{versionDialog.name}</p>
              </div>
              <IconButton title="닫기" onClick={() => setVersionDialog(null)}><X className="h-4 w-4" /></IconButton>
            </DialogHeader>
            <DialogBody>
              {versionDialog.manageable !== false && !readOnly && (
                <div className="mb-3 flex justify-end">
                  <button
                    type="button"
                    onClick={() => versionInputRef.current?.click()}
                    disabled={busy || !fileContentAvailable(versionDialog)}
                    title={!fileContentAvailable(versionDialog) ? scanUnavailableMessage(versionDialog) : '새 버전 업로드'}
                    className="inline-flex h-9 items-center gap-2 rounded-md border border-slate-300 px-3 text-sm font-medium hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <Upload className="h-4 w-4" />새 버전 업로드
                  </button>
                  <input
                    ref={versionInputRef}
                    type="file"
                    accept={ACCEPTED_FILE_TYPES}
                    className="hidden"
                    onChange={(event) => {
                      uploadVersion(event.target.files?.[0])
                      event.target.value = ''
                    }}
                  />
                </div>
              )}
              {versionsLoading ? (
                <div className="flex min-h-40 items-center justify-center"><LoaderCircle className="h-6 w-6 animate-spin text-slate-400" /></div>
              ) : (
                <ol className="divide-y divide-slate-100 border-y border-slate-200">
                  {versions.map((version) => (
                    <li key={version.current ? `current-${version.versionNumber}` : version.id} className="flex items-center gap-3 py-3">
                      <History className="h-5 w-5 shrink-0 text-slate-400" />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-slate-800">
                          버전 {version.versionNumber}{version.current && <span className="ml-2 text-xs text-cyan-700">현재</span>}
                        </p>
                        <p className="mt-0.5 truncate text-xs text-slate-500">{version.createdByName} · {formatBytes(version.sizeBytes)} · {formatDate(version.createdAt)}</p>
                        <div className="mt-1"><ScanStatusBadge status={version.scanStatus} /></div>
                      </div>
                      <IconButton title={fileContentAvailable(version) ? '버전 다운로드' : scanUnavailableMessage(version)} disabled={busy || !fileContentAvailable(version)} onClick={() => downloadVersion(version)}><Download className="h-4 w-4" /></IconButton>
                      {!version.current && versionDialog.manageable !== false && !readOnly && (
                        <IconButton title={fileContentAvailable(version) ? '이 버전으로 복원' : scanUnavailableMessage(version)} disabled={busy || !fileContentAvailable(version)} onClick={() => restoreVersion(version)}><RotateCcw className="h-4 w-4" /></IconButton>
                      )}
                    </li>
                  ))}
                </ol>
              )}
            </DialogBody>
          </DialogPanel>
        </DialogBackdrop>
      )}

      {preview && (
        <DialogBackdrop onMouseDown={(event) => event.target === event.currentTarget && setPreview(null)} className="items-center p-3">
          <DialogPanel className="max-w-5xl" aria-labelledby="org-library-preview-title">
            <DialogHeader>
              <h2 id="org-library-preview-title" className="truncate font-semibold text-slate-900">{preview.item.name}</h2>
              <div className="flex gap-1">
                <IconButton title="다운로드" onClick={() => downloadFile(preview.item)}><Download className="h-4 w-4" /></IconButton>
                <IconButton title="닫기" onClick={() => setPreview(null)}><X className="h-4 w-4" /></IconButton>
              </div>
            </DialogHeader>
            <DialogBody className="flex min-h-[65vh] items-center justify-center bg-slate-100 p-2">
              {preview.type?.startsWith('image/') ? <img src={preview.url} alt={preview.item.name} className="max-h-[70vh] max-w-full object-contain" />
                : <iframe src={preview.url} title={preview.item.name} className="h-[70vh] w-full border-0 bg-white" />}
            </DialogBody>
          </DialogPanel>
        </DialogBackdrop>
      )}
      {permissionFolder && (
        <OrgLibraryPermissionDialog
          apiRoot={apiRoot}
          rootOrgId={selectedRootOrgId}
          folder={permissionFolder}
          onClose={() => setPermissionFolder(null)}
          onSaved={loadWorkspaceSupport}
        />
      )}

      {collaborationFile && (
        <OrgLibraryCollaborationPanel
          apiRoot={apiRoot}
          rootOrgId={selectedRootOrgId}
          file={collaborationFile}
          onClose={() => setCollaborationFile(null)}
        />
      )}

      {contextMenu && (
        <LibraryContextMenu
          context={contextMenu}
          readOnly={readOnly}
          busy={busy}
          onClose={() => setContextMenu(null)}
          onOpenFolder={openFolder}
          onPreview={previewFile}
          onDownload={downloadFile}
          onCollaboration={setCollaborationFile}
          onShareFolder={setPermissionFolder}
          onVersions={openVersions}
          onCopy={copyItem}
          onStar={patchItem}
          onMove={(kind, item) => { setMoveDialog({ kind, item }); setMoveTarget('') }}
          onRename={(kind, item) => { setEditDialog({ mode: 'rename', kind, item }); setEditValue(item.name) }}
          onTrash={trashItem}
        />
      )}


    </div>
  )
}

function LibraryContextMenu(props) {
  const { context, readOnly, busy } = props
  const { kind, item } = context
  const manageable = item.manageable !== false
  const contentUnavailable = kind === 'file' && !fileContentAvailable(item)
  const run = (handler) => {
    props.onClose()
    handler()
  }
  const menuButton = 'flex h-9 w-full items-center gap-3 px-3 text-left text-sm text-slate-700 hover:bg-slate-100 disabled:opacity-40'

  return (
    <div
      role="menu"
      onClick={(event) => event.stopPropagation()}
      className="fixed z-50 w-52 overflow-hidden rounded-md border border-slate-200 bg-white py-1 shadow-xl"
      style={{ left: context.x, top: context.y }}
    >
      {kind === 'folder' && <button type="button" className={menuButton} onClick={() => run(() => props.onOpenFolder(item))}><FolderOpen className="h-4 w-4" />열기</button>}
      {kind === 'file' && item.previewable && <button type="button" className={menuButton} disabled={busy} onClick={() => run(() => props.onPreview(item))}><Eye className="h-4 w-4" />미리보기</button>}
      {kind === 'file' && <button type="button" className={menuButton} disabled={busy || contentUnavailable} title={contentUnavailable ? scanUnavailableMessage(item) : undefined} onClick={() => run(() => props.onDownload(item))}><Download className="h-4 w-4" />다운로드</button>}
      {kind === 'file' ? (
        <button type="button" className={menuButton} onClick={() => run(() => props.onCollaboration(item))}><MessageSquare className="h-4 w-4" />파일 정보</button>
      ) : manageable && (
        <button type="button" className={menuButton} onClick={() => run(() => props.onShareFolder(item))}><Share2 className="h-4 w-4" />폴더 공유</button>
      )}
      {kind === 'file' && <button type="button" className={menuButton} onClick={() => run(() => props.onVersions(item))}><History className="h-4 w-4" />버전 관리</button>}
      {!readOnly && <button type="button" className={menuButton} disabled={busy || contentUnavailable} onClick={() => run(() => props.onCopy(kind, item))}><Copy className="h-4 w-4" />사본 만들기</button>}
      {!readOnly && manageable && (
        <>
          <div className="my-1 border-t border-slate-200" />
          <button type="button" className={menuButton} onClick={() => run(() => props.onStar(kind, item, { starred: !item.starred }))}><Star className={`h-4 w-4 ${item.starred ? 'fill-amber-400 text-amber-500' : ''}`} />{item.starred ? '중요 표시 해제' : '중요 표시'}</button>
          <button type="button" className={menuButton} onClick={() => run(() => props.onMove(kind, item))}><Move className="h-4 w-4" />이동</button>
          <button type="button" className={menuButton} onClick={() => run(() => props.onRename(kind, item))}><FileText className="h-4 w-4" />이름 변경</button>
          <button type="button" className={`${menuButton} text-red-700`} onClick={() => run(() => props.onTrash(kind, item))}><Trash2 className="h-4 w-4" />휴지통으로 이동</button>
        </>
      )}
    </div>
  )
}


function ItemActions(props) {
  const { kind, item, readOnly, activeView, busy } = props
  const manageable = item.manageable !== false
  const contentUnavailable = kind === 'file' && !fileContentAvailable(item)
  if (activeView === 'TRASH') {
    return (
      <div className="flex flex-wrap items-center justify-end gap-0.5">
        {!readOnly && manageable && <IconButton title="복원" disabled={busy} onClick={() => props.onRestore(kind, item)}><RotateCcw className="h-4 w-4" /></IconButton>}
        {manageable && <IconButton title="영구 삭제" disabled={busy} onClick={() => props.onPermanentDelete(kind, item)} className="text-red-600"><Trash2 className="h-4 w-4" /></IconButton>}
      </div>
    )
  }
  return (
    <div className="flex flex-wrap items-center justify-end gap-0.5">
      {kind === 'file' && item.previewable && <IconButton title="미리보기" disabled={busy} onClick={() => props.onPreview(item)}><Eye className="h-4 w-4" /></IconButton>}
      {kind === 'file' && <IconButton title={contentUnavailable ? scanUnavailableMessage(item) : '다운로드'} disabled={busy || contentUnavailable} onClick={() => props.onDownload(item)}><Download className="h-4 w-4" /></IconButton>}
      {kind === 'file' && <IconButton title="파일 정보" disabled={busy} onClick={() => props.onCollaboration(item)}><MessageSquare className="h-4 w-4" /></IconButton>}
      {kind === 'file' && <IconButton title="버전 관리" disabled={busy} onClick={() => props.onVersions(item)}><History className="h-4 w-4" /></IconButton>}
      {kind === 'folder' && manageable && <IconButton title="폴더 공유" disabled={busy} onClick={() => props.onShareFolder(item)}><Share2 className="h-4 w-4" /></IconButton>}
      {!readOnly && <IconButton title="사본 만들기" disabled={busy || contentUnavailable} onClick={() => props.onCopy(kind, item)}><Copy className="h-4 w-4" /></IconButton>}
      {!readOnly && manageable && (
        <>
          <IconButton title={item.starred ? '중요 표시 해제' : '중요 표시'} disabled={busy} onClick={() => props.onStar(kind, item, { starred: !item.starred })}><Star className={`h-4 w-4 ${item.starred ? 'fill-amber-400 text-amber-500' : ''}`} /></IconButton>
          <IconButton title="이동" disabled={busy} onClick={() => props.onMove(kind, item)}><Move className="h-4 w-4" /></IconButton>
          <IconButton title="이름 변경" disabled={busy} onClick={() => props.onRename(kind, item)}><FileText className="h-4 w-4" /></IconButton>
          <IconButton title="휴지통으로 이동" disabled={busy} onClick={() => props.onTrash(kind, item)} className="text-red-600"><Trash2 className="h-4 w-4" /></IconButton>
        </>
      )}
      <IconButton title="더보기" onClick={(event) => props.onContextMenu(event, kind, item)}><MoreHorizontal className="h-4 w-4" /></IconButton>
    </div>
  )
}

function ItemTable(props) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[840px] table-fixed text-sm">
        <thead>
          <tr className="border-b border-slate-200 text-left text-xs font-medium text-slate-500">
            <th className="w-10 py-2"><input type="checkbox" aria-label="현재 페이지 전체 선택" checked={props.items.length > 0 && props.selected.size === props.items.length} onChange={props.onSelectAll} /></th>
            <th className="py-2">이름</th>
            <th className="w-32 py-2">소유자</th>
            <th className="w-24 py-2">안전 검사</th>
            <th className="w-28 py-2">크기</th>
            <th className="w-40 py-2">수정일</th>
            <th className="w-64 py-2 text-right">작업</th>
          </tr>
        </thead>
        <tbody>
          {props.items.map(({ kind, item }) => {
            const key = itemKey(kind, item.id)
            const isDropTarget = kind === 'folder' && String(props.dropTargetFolderId) === String(item.id)
            return (
              <tr
                key={key}
                draggable={!props.readOnly && item.manageable !== false}
                onDragStart={(event) => props.onDragStart(event, kind, item)}
                onDragEnd={props.onDragEnd}
                onDragOver={kind === 'folder' ? (event) => props.onFolderDragOver(event, item) : undefined}
                onDragLeave={kind === 'folder' ? (event) => props.onFolderDragLeave(event, item) : undefined}
                onDrop={kind === 'folder' ? (event) => props.onFolderDrop(event, item) : undefined}
                onContextMenu={(event) => props.onContextMenu(event, kind, item)}
                className={`border-b border-slate-100 hover:bg-slate-50 ${isDropTarget ? 'bg-cyan-50 ring-2 ring-inset ring-cyan-500' : props.selected.has(key) ? 'bg-cyan-50/60' : ''}`}
              >
                <td className="py-2"><input type="checkbox" aria-label={`${item.name} 선택`} checked={props.selected.has(key)} onChange={() => props.onSelect(kind, item.id)} /></td>
                <td className="min-w-0 py-2 pr-3">
                  <button type="button" onClick={() => kind === 'folder' && props.onOpenFolder(item)} onDoubleClick={() => kind === 'folder' && props.onOpenFolder(item)} className="flex w-full min-w-0 items-center gap-2 text-left">
                    {itemIcon(item, kind)}
                    <span className="truncate font-medium text-slate-800">{item.name}</span>
                    {item.starred && <Star className="h-3.5 w-3.5 shrink-0 fill-amber-400 text-amber-500" />}
                  </button>
                </td>
                <td className="truncate py-2 pr-2 text-slate-500">{item.ownerName}</td>
                <td className="py-2">{kind === 'file' ? <ScanStatusBadge status={item.scanStatus} /> : <span className="text-slate-300">-</span>}</td>
                <td className="py-2 text-slate-500">{kind === 'file' ? formatBytes(item.sizeBytes) : `${item.itemCount || 0}개`}</td>
                <td className="py-2 text-slate-500">{formatDate(item.updatedAt)}</td>
                <td className="py-2"><ItemActions {...props} kind={kind} item={item} /></td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function ItemGrid(props) {
  return (
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
      {props.items.map(({ kind, item }) => {
        const key = itemKey(kind, item.id)
        const isDropTarget = kind === 'folder' && String(props.dropTargetFolderId) === String(item.id)
        return (
          <article
            key={key}
            draggable={!props.readOnly && item.manageable !== false}
            onDragStart={(event) => props.onDragStart(event, kind, item)}
            onDragEnd={props.onDragEnd}
            onDragOver={kind === 'folder' ? (event) => props.onFolderDragOver(event, item) : undefined}
            onDragLeave={kind === 'folder' ? (event) => props.onFolderDragLeave(event, item) : undefined}
            onDrop={kind === 'folder' ? (event) => props.onFolderDrop(event, item) : undefined}
            onContextMenu={(event) => props.onContextMenu(event, kind, item)}
            className={`relative min-w-0 rounded-md border p-3 ${isDropTarget ? 'border-cyan-500 bg-cyan-50 ring-2 ring-cyan-500' : props.selected.has(key) ? 'border-cyan-400 bg-cyan-50/60' : 'border-slate-200'}`}
          >
            <input type="checkbox" aria-label={`${item.name} 선택`} checked={props.selected.has(key)} onChange={() => props.onSelect(kind, item.id)} className="absolute left-3 top-3" />
            <button type="button" onClick={() => kind === 'folder' && props.onOpenFolder(item)} onDoubleClick={() => kind === 'folder' && props.onOpenFolder(item)} className="mt-8 flex w-full min-w-0 items-center gap-3 text-left">
              {itemIcon(item, kind, 'h-8 w-8')}
              <span className="truncate text-sm font-semibold text-slate-800">{item.name}</span>
            </button>
            {kind === 'file' && <div className="mt-2"><ScanStatusBadge status={item.scanStatus} /></div>}
            <div className="mt-3 flex items-center justify-between border-t border-slate-100 pt-2 text-xs text-slate-500">
              <span>{kind === 'file' ? formatBytes(item.sizeBytes) : `${item.itemCount || 0}개`}</span>
              <ItemActions {...props} kind={kind} item={item} />
            </div>
          </article>
        )
      })}
    </div>
  )
}

function ActivityList({ activities }) {
  if (!activities.length) {
    return <div className="flex min-h-[360px] flex-col items-center justify-center text-sm text-slate-500"><History className="mb-3 h-10 w-10 text-slate-300" />활동 기록이 없습니다.</div>
  }
  return (
    <ol className="divide-y divide-slate-100">
      {activities.map((activity) => (
        <li key={activity.id} className="flex gap-3 py-3">
          <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-100"><Activity className="h-4 w-4 text-slate-500" /></div>
          <div className="min-w-0 flex-1">
            <p className="text-sm text-slate-800"><strong>{activity.actorName}</strong> · {activity.actionLabel}</p>
            {activity.itemName && <p className="mt-0.5 truncate text-sm text-slate-600">{activity.itemTypeLabel} · {activity.itemName}</p>}
          </div>
          <time className="shrink-0 text-xs text-slate-500">{formatDate(activity.createdAt)}</time>
        </li>
      ))}
    </ol>
  )
}
